import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent, type DragEvent } from "react";
import { CanvasEditor, type CanvasEditorHandle, type SupporterCutout } from "./components/CanvasEditor";
import { CampaignDisclosure } from "./components/CampaignDisclosure";
import { Gallery, GalleryEmpty } from "./components/Gallery";
import {
  DownloadIcon,
  GalleryIcon,
  LockIcon,
  GroupIcon,
  ProfileIcon,
  RefreshIcon,
  ShareIcon,
  StoryIcon,
  UploadIcon,
} from "./components/Icons";
import { campaign, campaignAsset, type CastMember } from "./config/campaign";
import type { LayoutKey, Transform } from "./lib/geometry";
import { maxPartnersFor } from "./lib/stage";
import { CutoutError, requestCutout } from "./lib/cutout";
import { downloadFile, shareFile } from "./lib/deliver";

const initialTransform: Transform = { zoom: 1, panX: 0, panY: 0 };

/** Endereço da galeria. O nginx devolve o index.html em qualquer caminho, então a
 *  navegação é do próprio app — mas o link da bio aponta direto para cá. */
const GALLERY_PATH = "/artes";

type Toast = { kind: "success" | "error"; message: string } | null;
type View = "studio" | "gallery";

const gallery = campaign.gallery?.items?.length ? campaign.gallery : undefined;

/**
 * A rota existe mesmo sem arte publicada. O link da bio é impresso e reencaminhado: mandá-lo
 * para o estúdio calado faria quem tocou em "Artes da campanha" achar que errou o botão.
 * Sem arte, a rota diz que as artes ainda vêm — e oferece o santinho no lugar.
 */
function viewFromPath(pathname: string): View {
  const clean = pathname.replace(/\/+$/, "").toLowerCase();
  return clean === GALLERY_PATH ? "gallery" : "studio";
}

function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<CanvasEditorHandle>(null);
  const [layout, setLayout] = useState<LayoutKey>("avatar");
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [transforms, setTransforms] = useState<Record<LayoutKey, Transform>>({
    story: { ...initialTransform },
    avatar: { ...initialTransform },
    cast: { ...initialTransform },
  });
  const [partnerIds, setPartnerIds] = useState<string[]>([]);
  const [cutout, setCutout] = useState<SupporterCutout | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [castImages, setCastImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<"download" | "share" | null>(null);
  const [completed, setCompleted] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const [view, setView] = useState<View>(() => viewFromPath(window.location.pathname));

  const partners = useMemo(
    () => (campaign.cast?.partners || []).filter((member) => partnerIds.includes(member.id)),
    [partnerIds],
  );
  const partnerLimit = campaign.stage ? maxPartnersFor(campaign.stage) : 0;
  const castLayout = layout === "cast";

  useEffect(() => {
    if (!campaign.cast) return;
    const members = [campaign.cast.primary, ...campaign.cast.partners];
    let active = true;
    Promise.all(
      members.map(
        (member) =>
          new Promise<[string, HTMLImageElement] | null>((resolve) => {
            const image = new Image();
            image.onload = () => resolve([member.id, image]);
            image.onerror = () => resolve(null);
            image.src = campaignAsset(member.cutout);
          }),
      ),
    ).then((loaded) => {
      if (active) setCastImages(new Map(loaded.filter((item) => item !== null)));
    });
    return () => {
      active = false;
    };
  }, []);

  // Trocar de formato pode reduzir quantos parceiros cabem na arte.
  useEffect(() => {
    setPartnerIds((current) => (current.length > partnerLimit ? current.slice(0, partnerLimit) : current));
  }, [partnerLimit]);

  // Voltar do navegador é o gesto natural de quem chegou na galeria pelo link da bio.
  useEffect(() => {
    const onPop = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.title = view === "gallery" ? `Artes da campanha · ${campaign.candidate}` : campaign.seo.title;
  }, [view]);

  const showToast = useCallback((next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const goTo = useCallback((next: View) => {
    setView((current) => {
      if (current === next) return current;
      window.history.pushState(null, "", next === "gallery" ? GALLERY_PATH : "/");
      return next;
    });
  }, []);

  const galleryToast = useCallback(
    (kind: "success" | "error", message: string) => showToast({ kind, message }),
    [showToast],
  );

  const handleFrameError = useCallback(() => {
    showToast({ kind: "error", message: "A moldura não carregou. Atualize a página e tente novamente." });
  }, [showToast]);

  const choosePhoto = useCallback(() => inputRef.current?.click(), []);

  const prepareCutout = useCallback(
    async (file: File) => {
      setPreparing(true);
      try {
        setCutout(await requestCutout(file));
        showToast({ kind: "success", message: "Pronto! Você já está ao lado do Márcio." });
      } catch (error) {
        setCutout(null);
        setLayout("avatar");
        showToast({
          kind: "error",
          message:
            error instanceof CutoutError
              ? `${error.message} Enquanto isso, use Story ou Perfil.`
              : "Não foi possível montar a foto com o Márcio. Use Story ou Perfil por enquanto.",
        });
      } finally {
        setPreparing(false);
      }
    },
    [showToast],
  );

  function loadFile(file?: File) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast({ kind: "error", message: "Esse arquivo não é uma imagem. Escolha um JPG, PNG ou WEBP." });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showToast({ kind: "error", message: "A foto tem mais de 25 MB. Escolha uma imagem menor." });
      return;
    }

    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setPhoto(image);
      setSourceFile(file);
      setCutout(null);
      setTransforms({ story: { ...initialTransform }, avatar: { ...initialTransform }, cast: { ...initialTransform } });
      setCompleted(false);
      URL.revokeObjectURL(url);
      if (castLayout) {
        void prepareCutout(file);
      } else {
        showToast({ kind: "success", message: "Foto pronta. Agora ajuste o enquadramento." });
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      showToast({ kind: "error", message: "Não conseguimos abrir essa foto. Tente outra imagem." });
    };
    image.src = url;
  }

  function chooseLayout(next: LayoutKey) {
    setLayout(next);
    setCompleted(false);
    if (next === "cast" && !cutout && sourceFile && !preparing) void prepareCutout(sourceFile);
  }

  function togglePartner(id: string) {
    setCompleted(false);
    setPartnerIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= partnerLimit) return current;
      return [...current, id];
    });
  }

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    loadFile(event.target.files?.[0]);
    event.target.value = "";
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    loadFile(event.dataTransfer.files?.[0]);
  }

  async function makeFile() {
    if (!photo) throw new Error("Escolha uma foto antes de gerar o santinho.");
    const blob = await editorRef.current?.exportBlob();
    if (!blob) throw new Error("Não foi possível gerar a imagem.");
    return new File([blob], `santinho-${campaign.slug}-${layout}.png`, { type: "image/png" });
  }

  async function download() {
    setBusy("download");
    try {
      downloadFile(await makeFile());
      setCompleted(true);
      showToast({ kind: "success", message: "Santinho baixado. Agora é só compartilhar!" });
    } catch (error) {
      showToast({ kind: "error", message: error instanceof Error ? error.message : "Não foi possível baixar." });
    } finally {
      setBusy(null);
    }
  }

  async function share() {
    setBusy("share");
    try {
      const outcome = await shareFile(await makeFile(), {
        title: `Meu Santinho com ${campaign.shortName}`,
        text: campaign.shareText,
        url: window.location.href,
      });
      if (outcome === "aborted") return;
      if (outcome === "copied") {
        showToast({ kind: "success", message: "Texto e link copiados para você compartilhar." });
      }
      setCompleted(true);
    } catch {
      showToast({ kind: "error", message: "O compartilhamento não abriu. Baixe a imagem e envie pelo WhatsApp." });
    } finally {
      setBusy(null);
    }
  }

  const step = completed ? 3 : photo ? 2 : 1;
  const transform = transforms[layout];
  const displayNumber = `${campaign.number.slice(0, 2)}.${campaign.number.slice(2)}`;
  // Só o recorte com fundo transparente serve aqui: o portrait comum é um retângulo opaco
  // e colado no canto do painel pareceria um bloco recortado a tesoura.
  const candidateCutout = campaign.cast?.primary.cutout;
  const campaignTheme = {
    "--candidate-portrait": candidateCutout ? `url(${campaignAsset(candidateCutout)})` : "none",
    "--blue-950": campaign.theme.deep,
    "--blue-900": campaign.theme.surface,
    "--blue-650": campaign.theme.action,
    "--yellow": campaign.theme.accent,
    "--green": campaign.theme.success,
    "--ink": campaign.theme.ink,
    "--muted": campaign.theme.muted,
    "--surface": campaign.theme.surface,
    "--surface-raised": campaign.theme.surfaceRaised,
  } as CSSProperties;

  return (
    <main className="app-shell" style={campaignTheme}>
      <section className={`campaign-panel ${candidateCutout ? "campaign-panel--portrait" : ""}`} aria-label="Campanha">
        <div className="halftone halftone--one" />
        <div className="halftone halftone--two" />
        <div className="candidate-lockup">
          <div className="party-mark" aria-label={`Partido ${campaign.party}`}>
            <strong>{campaign.party}</strong>
            <small>PARTIDO LIBERAL</small>
          </div>
          <div className="candidate-copy">
            <p className="candidate-name">{campaign.candidate}</p>
            <p className="candidate-office">{campaign.office}</p>
          </div>
        </div>
        <div className="campaign-message">
          <p className="eyebrow">{campaign.slogan}</p>
          <h1>{displayNumber}</h1>
          <p>{campaign.supportingText}</p>
        </div>
        <div className="campaign-swoosh" aria-hidden="true"><span /><span /></div>
      </section>

      <section
        className={`studio-panel ${view === "gallery" ? "studio-panel--gallery" : ""}`}
        aria-label={view === "gallery" ? "Artes da campanha" : "Editor de santinho"}
      >
        <header className="mobile-lockup">
          <div className="party-mark party-mark--compact" aria-hidden="true">
            <strong>{campaign.party}</strong>
          </div>
          <div>
            <span>{campaign.shortName}</span>
            <strong>{displayNumber}</strong>
          </div>
          <div className="brand-kicker" aria-label="Eu voto">
            <b>EU</b><em>VOTO!</em>
          </div>
        </header>

        {(gallery || view === "gallery") && (
          <nav className="mode-switcher" aria-label="Seções">
            <button
              type="button"
              className={view === "studio" ? "active" : ""}
              aria-current={view === "studio" ? "page" : undefined}
              onClick={() => goTo("studio")}
            ><ProfileIcon /> Meu santinho</button>
            <button
              type="button"
              className={view === "gallery" ? "active" : ""}
              aria-current={view === "gallery" ? "page" : undefined}
              onClick={() => goTo("gallery")}
            ><GalleryIcon /> Artes prontas</button>
          </nav>
        )}

        {view === "gallery" ? (
          <>
            {gallery ? (
              <Gallery items={gallery.items} title={gallery.title} note={gallery.note} onToast={galleryToast} />
            ) : (
              <GalleryEmpty onStudio={() => goTo("studio")} />
            )}
            <CampaignDisclosure />
          </>
        ) : (
          <>
        <ol className="progress" aria-label={`Etapa ${step} de 3`}>
          {["Foto", castLayout ? "Elenco" : "Ajuste", "Compartilhe"].map((label, index) => {
            const number = index + 1;
            return (
              <li key={label} className={number === step ? "active" : number < step ? "done" : ""}>
                <span>{number < step ? "✓" : number}</span>{label}
              </li>
            );
          })}
        </ol>

        <div
          className={`layout-switcher ${campaign.cast ? "layout-switcher--three" : ""}`}
          role="tablist"
          aria-label="Formato da imagem"
        >
          <button
            type="button"
            role="tab"
            aria-selected={layout === "story"}
            className={layout === "story" ? "active" : ""}
            onClick={() => chooseLayout("story")}
          ><StoryIcon /> Story</button>
          <button
            type="button"
            role="tab"
            aria-selected={layout === "avatar"}
            className={layout === "avatar" ? "active" : ""}
            onClick={() => chooseLayout("avatar")}
          ><ProfileIcon /> Perfil</button>
          {campaign.cast && (
            <button
              type="button"
              role="tab"
              aria-selected={layout === "cast"}
              className={layout === "cast" ? "active" : ""}
              onClick={() => chooseLayout("cast")}
            ><GroupIcon /> Com o {campaign.shortName.split(" ")[0]}</button>
          )}
        </div>

        {campaign.cast && castLayout && (
          <fieldset className="cast-picker">
            <legend>Chame mais alguém para a foto</legend>
            <ul>
              {campaign.cast.partners.map((member: CastMember) => {
                const selected = partnerIds.includes(member.id);
                const blocked = !selected && partnerIds.length >= partnerLimit;
                return (
                  <li key={member.id}>
                    <button
                      type="button"
                      className={`cast-chip ${selected ? "selected" : ""}`}
                      aria-pressed={selected}
                      disabled={blocked}
                      onClick={() => togglePartner(member.id)}
                    >
                      <span className="cast-chip__name">{member.name}</span>
                      <span className="cast-chip__number">{member.number}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p aria-live="polite">
              {partnerLimit === 0
                ? "Neste formato aparece só você e o candidato."
                : partnerIds.length >= partnerLimit
                  ? `Você já escolheu ${partnerLimit}. Desmarque alguém para trocar.`
                  : `Você pode escolher até ${partnerLimit}.`}
            </p>
          </fieldset>
        )}


        <div className={`editor-zone editor-zone--${layout}`} onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
          <CanvasEditor
            ref={editorRef}
            layout={layout}
            image={photo}
            cutout={cutout}
            partners={partners}
            castImages={castImages}
            onChoose={choosePhoto}
            transform={transform}
            onTransformChange={(next) => setTransforms((current) => ({ ...current, [layout]: next }))}
            onFrameError={handleFrameError}
          />
        </div>

        {!castLayout && (
        <div className="adjustments">
          <label htmlFor="zoom">Zoom</label>
          <input
            id="zoom"
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={transform.zoom}
            disabled={!photo}
            onChange={(event) => setTransforms((current) => ({
              ...current,
              [layout]: { ...current[layout], zoom: Number(event.target.value) },
            }))}
          />
          <span>{Math.round(transform.zoom * 100)}%</span>
          <p>{photo ? "Arraste ou faça pinça para ajustar" : "Escolha uma foto para começar"}</p>
        </div>
        )}

        <div className="action-area">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} hidden />
          {!photo ? (
            <button className="primary-action" type="button" onClick={choosePhoto}>
              <UploadIcon /> Escolher foto
            </button>
          ) : (
            <div className="ready-actions">
              <button className="icon-action" type="button" onClick={choosePhoto} aria-label="Trocar foto" title="Trocar foto">
                <RefreshIcon />
              </button>
              <button className="primary-action" type="button" onClick={download} disabled={busy !== null || preparing}>
                <DownloadIcon /> {preparing ? "Montando a foto..." : busy === "download" ? "Gerando imagem..." : "Baixar imagem"}
              </button>
              <button className="icon-action" type="button" onClick={share} disabled={busy !== null} aria-label="Compartilhar santinho" title="Compartilhar santinho">
                <ShareIcon />
              </button>
            </div>
          )}
          <p className="privacy">
            <LockIcon />
            {castLayout
              ? "Sua foto é enviada só para montar a arte e não fica guardada"
              : "Sua foto fica só neste aparelho"}
          </p>
          <CampaignDisclosure />
        </div>
          </>
        )}
      </section>

      <div className={`toast ${toast ? "toast--visible" : ""} ${toast?.kind === "error" ? "toast--error" : ""}`} role="status" aria-live="polite">
        {toast?.message}
      </div>
    </main>
  );
}

export default App;
